<template>
  <div class="filters-selected">
    <div v-if="false" class="filters-selected-item">
      <span class="filters-selected-label">{{ $t("Leads.CompanyPlaceholder") }}:</span>
      <span class="filters-selected-values">
        <div class="filters-selected-value">
          <b-button v-for="company in selectedCompanies" :key="company.Id" type="is-primary is-small is-icon" :value="company.Id" @click="onSelectedCompanyRemove(company.Id)">
            <span>{{ company.Title }}</span>
            <icon-base width="7.71" height="7.71" view-box="0 0 7.71 7.71" icon-name="times-small">
              <icon-times-small />
            </icon-base>
          </b-button>
        </div>
      </span>
    </div>
    <div v-if="selectedCampaigns.length" class="filters-selected-item">
      <span class="filters-selected-label">{{ $t("Campaigns.Title") }}:</span>
      <span class="filters-selected-values">
        <div class="filters-selected-value">
          <b-button v-for="campaign in selectedCampaigns" :key="campaign.ChannelId" type="is-primary is-small is-icon" :value="campaign.ChannelId" @click="onSelectedCampaignRemove(campaign.ChannelId)">
            <span>{{ campaign.ChannelTitle }}</span>
            <icon-base width="7.71" height="7.71" view-box="0 0 7.71 7.71" icon-name="times-small">
              <icon-times-small />
            </icon-base>
          </b-button>
        </div>
      </span>
    </div>
    <div v-if="selectedProjects.length" class="filters-selected-item">
      <span class="filters-selected-label">{{ $t("Leads.ProjectPerformancePlaceholder") }}:</span>
      <span class="filters-selected-values">
        <div class="filters-selected-value">
          <b-button v-for="project in selectedProjects" :key="project.Id" type="is-primary is-small is-icon" :value="project.Id" @click="onSelectedProjectRemove(project.Id)">
            <span>{{ project.Title }}</span>
            <icon-base width="7.71" height="7.71" view-box="0 0 7.71 7.71" icon-name="times-small">
              <icon-times-small />
            </icon-base>
          </b-button>
        </div>
      </span>
    </div>
    <div v-if="selectedChannelMetadataTypes.length" class="filters-selected-item">
      <span class="filters-selected-label">{{ $t("Leads.ChannelMetadataPlaceholder") }}:</span>
      <span class="filters-selected-values">
        <div class="filters-selected-value">
          <b-button
            v-for="channelMetadataType in selectedChannelMetadataTypes"
            :key="channelMetadataType.Id"
            type="is-primary is-small is-icon"
            :value="channelMetadataType.Id"
            @click="onSelectedChannelMetadataTypeRemove(channelMetadataType.Id)"
          >
            <span>{{ channelMetadataType.Title }}</span>
            <icon-base width="7.71" height="7.71" view-box="0 0 7.71 7.71" icon-name="times-small">
              <icon-times-small />
            </icon-base>
          </b-button>
        </div>
      </span>
    </div>
    <b-button type="is-warning" size="is-small" class="reset-filters-btn" :label="$t('ResetFilters')"
          v-if="hasFilters"
          @click="resetFilters()" />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { MultiselectItem } from "@/models/MultiselectItem";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignGridView } from "@/models/campaigns/CampaignGridView";
import { CampaignStatus } from "@/models/campaigns/CampaignStatus";
import { AvailableChannelMetadataTypesMode } from "@/models/campaigns/ChannelType";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import IconBase from "../IconBase.vue";
import IconTimesSmall from "../icons/IconTimesSmall.vue";

const namespace = "campaignsModule";

@Component({
  components: {
    IconBase,
    IconTimesSmall,
  },
})
export default class CampaignsFiltersSelected extends Vue {
  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("campaignsWithStats", { namespace }) campaignsWithStats!: CampaignGridView[];
  @Getter("companies", { namespace }) companies!: MultiselectItem[];
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Getter("isMediaChannel", { namespace }) isMediaChannel!: boolean;
  @Getter("projects", { namespace }) projects!: MultiselectItem[];
  @Getter("selectedCampaignsIds", { namespace }) selectedCampaignsIds!: number[];
  @Getter("selectedCampaignStatus", { namespace }) selectedCampaignStatus!: CampaignStatus | null;
  @Getter("selectedChannelMetadataTypesIds", { namespace }) selectedChannelMetadataTypesIds!: number[];
  @Getter("selectedCompaniesIds", { namespace }) selectedCompaniesIds!: number[];
  @Getter("selectedProjectsIds", { namespace }) selectedProjectsIds!: number[];
  @Action("setSelectedCampaignsIds", { namespace }) setSelectedCampaignsIds!: (selectedCampaignsIds: number[]) => void;
  @Action("setSelectedChannelMetadataTypesIds", { namespace }) setSelectedChannelMetadataTypesIds!: (selectedChannelMetadataTypesIds: number[]) => void;
  @Action("setSelectedCompaniesIds", { namespace }) setSelectedCompaniesIds!: (selectedCompaniesIds: number[]) => void;
  @Action("setSelectedProjectsIds", { namespace }) setSelectedProjectsIds!: (selectedProjectsIds: number[]) => void;
  
  get hasFilters(): boolean {
    return this.selectedCampaignsIds.length > 0
          || this.selectedCompanies.length > 0
          || this.selectedChannelMetadataTypes.length > 0
          || this.selectedProjectsIds.length > 0
          || !HelperService.compareDateArrays(this.datePeriod, HelperService.getThisMonthPeriod())
          || this.selectedCampaignStatus !== null && this.selectedCampaignStatus !== CampaignStatus.Active;
  }

  get selectedCampaigns(): CampaignGridView[] {
    if (!this.campaignsWithStats) {
      return [];
    }
    return this.campaignsWithStats.filter((i: CampaignGridView) => this.selectedCampaignsIds.indexOf((i.ChannelId as any) as number) >= 0);
  }

  get selectedChannelMetadataTypes(): MultiselectItem[] {
    const mode: AvailableChannelMetadataTypesMode = this.isMediaChannel ? AvailableChannelMetadataTypesMode.MediaCampaigns : AvailableChannelMetadataTypesMode.Campaigns;
    return CampaignService.getChannelMetadataTypes(mode).filter((i: MultiselectItem) => this.selectedChannelMetadataTypesIds.indexOf(+i.Id) >= 0);
  }

  get selectedCompanies(): MultiselectItem[] {
    return this.companies.filter((i: MultiselectItem) => this.selectedCompaniesIds.indexOf((i.Id as any) as number) >= 0);
  }

  get selectedProjects(): MultiselectItem[] {
    return this.projects.filter((i: MultiselectItem) => this.selectedProjectsIds.indexOf((i.Id as any) as number) >= 0);
  }

  onSelectedCampaignRemove(selectedCampaignId: number): void {
    const selectedCampaignsIdsNew: number[] = this.selectedCampaignsIds.filter((i) => i !== selectedCampaignId);
    this.setSelectedCampaignsIds(selectedCampaignsIdsNew);
  }

  onSelectedChannelMetadataTypeRemove(selectedChannelMetadataTypeId: number): void {
    const selectedChannelMetadataTypesIdsNew: number[] = this.selectedChannelMetadataTypesIds.filter((i) => i !== +selectedChannelMetadataTypeId);
    this.setSelectedChannelMetadataTypesIds(selectedChannelMetadataTypesIdsNew);
  }

  onSelectedCompanyRemove(selectedCompaniesId: number): void {
    const selectedCompaniesIdsNew: number[] = this.selectedCompaniesIds.filter((i) => i !== selectedCompaniesId);
    this.setSelectedCompaniesIds(selectedCompaniesIdsNew);
  }

  onSelectedProjectRemove(selectedProjectId: number): void {
    const selectedProjectsIdsNew: number[] = this.selectedProjectsIds.filter((i) => i !== selectedProjectId);
    this.setSelectedProjectsIds(selectedProjectsIdsNew);
  }

  resetFilters(): void {
    CampaignService.resetFilters(this.campaignsPageData.CompanyId !== 0);
  }
}
</script>
